import type { RiskClassCode } from './risk-classes'

/**
 * The dimensions of the RCL (Recommended Credit Limit) multiplier grid. The set of keys is
 * app-defined (these constants); only the multiplier *values* live in the database. The frontend
 * multiplies these into every permutation so the full grid is always shown - each cell filled from
 * the database when a value exists, otherwise left empty for the user to enter.
 */

/** Payment terms (SAP ZTERM) the grid supports. */
export const RCL_MULTIPLIER_PAYMENT_TERMS = [
  'N000',
  'B007',
  'B014',
  'B021',
  'B028',
  'B030',
] as const
export type RclMultiplierPaymentTerm = (typeof RCL_MULTIPLIER_PAYMENT_TERMS)[number]

/**
 * Risk class codes the grid supports, ordered by natural severity (least → most risky): Very Low,
 * Low, Medium, High. Not Provisional/Block. This order drives the default grid + sort so risk reads
 * low → high rather than by opaque code.
 */
export const RCL_MULTIPLIER_RISK_CLASS_CODES = [
  'Z06',
  'Z04',
  'Z03',
  'Z02',
] as const satisfies readonly RiskClassCode[]

/** PAD buckets. */
export const RCL_MULTIPLIER_PAD_VALUES = [1, 2, 3, 4, 5] as const
export type RclMultiplierPad = (typeof RCL_MULTIPLIER_PAD_VALUES)[number]

export type RclMultiplierKey = {
  paymentTerm: RclMultiplierPaymentTerm
  riskClassCode: RiskClassCode
  pad: RclMultiplierPad
}

/** Every supported (payment term × risk class × PAD) permutation - the keys, without values. */
export const RCL_MULTIPLIER_KEYS: RclMultiplierKey[] = RCL_MULTIPLIER_PAYMENT_TERMS.flatMap(
  (paymentTerm) =>
    RCL_MULTIPLIER_RISK_CLASS_CODES.flatMap((riskClassCode) =>
      RCL_MULTIPLIER_PAD_VALUES.map((pad) => ({ paymentTerm, riskClassCode, pad })),
    ),
)

/** Stable identity for a multiplier cell - used to match generated keys against persisted rows. */
export function rclMultiplierKey(key: {
  paymentTerm: string
  riskClassCode: string
  pad: number
}): string {
  return `${key.paymentTerm}|${key.riskClassCode}|${key.pad}`
}
